// ./src/utils/api/GuestGroupService.ts
//
// Values within a guest-group set ("Team 1", "VIP"). Mirrors the eventsmagic-api
// GuestGroup SDL. `where.id` accepts one id or many — a bulk update (>1 id) may
// only change status (server-enforced).
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';

const FIELDS = `
  id
  guestGroupSetId
  name
  description
  color
  icon
  maxMembers
  bibPrefix
  bibStartNumber
  orderNo
  status
  rights
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query GuestGroupList($filter: GuestGroupFilter, $params: PaginationAndSorting) {
    guestGroupList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation GuestGroupCreate($params: GuestGroupInput) {
    guestGroupCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation GuestGroupUpdate($where: GuestGroupWhereInput, $params: GuestGroupInput) {
    guestGroupUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation GuestGroupRemove($where: GuestGroupWhereInput) {
    guestGroupRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

export interface GuestGroup {
  id?: string;
  guestGroupSetId?: string | null;
  name?: string | null;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  maxMembers?: number | null;
  /** BIB config (sport events): prefix (e.g. "MSP", "5K"). */
  bibPrefix?: string | null;
  /** BIB config: first number as a string ("0001" keeps digit width). */
  bibStartNumber?: string | null;
  orderNo?: number | null;
  status?: number | null;
  rights?: string[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface GuestGroupInput {
  guestGroupSetId?: string | null;
  name?: string | null;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  maxMembers?: number | null;
  bibPrefix?: string | null;
  bibStartNumber?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

export class GuestGroupService {
  static async list(guestGroupSetId: string, statuses?: number[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: { guestGroupSetId: [guestGroupSetId], status: statuses },
    });
  }

  static async create(params: GuestGroupInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  /** `id` may be a single id or an array (bulk — status-only). */
  static async update(id: string | string[], params: GuestGroupInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }
}
